import store from "../root/store/store";
import { isFormVisibleToContact } from "@/form/form-visibility";
import type { entityProcessConfig } from "../common/entity-process/signalr-process-config";
import { Enums } from "../common/helper/enums";
import { clientEntity } from "../common/model/entity/client-entity";
import { clientLocationEntity } from "../common/model/entity/client-location-entity";
import { contactEntity } from "../common/model/entity/contact-entity";
import { technicianEntity } from "../common/model/entity/technician-entity";
import { ticketEntity } from "../common/model/entity/ticket-entity";
import type { enumurationResponse } from "../common/model/enumeration";
import { TicketPaginationValues } from "../common/model/ticket/ticket-pagination-values";
import { EntityManager } from "../common/services/entity-manager";
import enumService from "../common/services/enum-service";
import ticketService from "../common/services/ticket-service";
import { ErrorTemplateBuilder } from "./error-template-builder";
import clientApiService from "../common/services/client-api-service";
import formApiService from "../common/services/form-api-service";
import { formEntity } from "../common/model/entity/form-entity";
import TechnicianServices from "../common/services/technician-service";
import { technicianGroupEntity } from "../common/model/entity/technician-group-entity";
import { DateTime } from "luxon";
import { CommonHelper } from "../common/helper/common-helper";
import { tagEntity } from "../common/model/entity/tag-entity";
import { ticketTypeSettingEntity } from "../common/model/entity/ticket-type-setting-entity";
import ticketApiService from "../common/services/ticket-api-service";
import ServiceProviderServices from "../common/services/service-provider-service";
import {
  ticketStatusEntity,
  ticketTypeEntity,
} from "../common/model/entity/enum-entities";
import { applyClientPortalUnreadStateOnTicketSync } from "../common/entity-service/ticket-entity-service";
import { bumpTicketSidebarListRevision } from "../ticket/ticket-sidebar-list-refresh";
import projectService from "../project/services/project-service";
import { ProjectPaginationValues } from "../common/model/project/project-pagination-values";
import { syncProjectListToStore } from "../project/project-signalr-sync";
import { bumpProjectSidebarListRevision } from "../project/project-sidebar-list-refresh";
import { updateProjectListSessionCache } from "../project/project-list-state";

export class EntitySyncManager {
  //#region Properties
  public static applicationTicks: any = null;
  //#endregion

  static async fillEnums() {
    try {
      const result: enumurationResponse = await enumService.Get();
      if (result) {
        ticketStatusEntity.create({ data: result.ticket.status });
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText("EntitySyncManager.fillEnums", e),
      );
    }
  }

  static async fillDummyRecords() {
    try {
      ticketEntity.create({ data: { ticketId: "0" } });
      contactEntity.create({ data: { contactId: 0 } });
      technicianEntity.insert({ data: { technicianId: 0 } });
      clientEntity.create({ data: { clientId: 0 } });
      clientLocationEntity.create({ data: { clientLocationId: 0 } });
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText("EntitySyncManager.fillEnums", e),
      );
    }
  }

  //#region Get Updated Application Tick
  static async getApplicationTicks(): Promise<any> {
    try {
      return await ticketService.getServiceProviderAllTick();
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.getApplicationTicks",
          e,
        ),
      );
    }
  }

  //#endregion

  /** Page size for Messages sidebar `cx/list` (server caps at 50). */
  static readonly ticketListPageSize = 50;

  static buildTicketListPagination(
    page: number,
    query = "",
  ): TicketPaginationValues {
    const contactId: number = store.getters["ContactModule/getContactId"];
    const hasAllTicketsTag: boolean =
      store.getters["ContactModule/getHasAllTicketsTag"];
    const clientId: number | null = store.getters["ContactModule/getClientId"];

    return new TicketPaginationValues(
      "UpdatedOn",
      true,
      page,
      EntitySyncManager.ticketListPageSize,
      null,
      [],
      // Merged and Cancelled only. Closed ("4") is left in on purpose: with it absent the server
      // returns closed tickets still inside the MSP's reopen window alongside the open ones.
      ["5", "7"],
      null,
      contactId,
      null,
      null,
      null,
      clientId,
      null,
      false,
      null,
      null,
      null,
      query,
      0,
      hasAllTicketsTag,
    );
  }

  static resolveTicketListHasMore(
    result: any,
    page: number,
    pageContentLength: number,
  ): boolean {
    const pageSize = EntitySyncManager.ticketListPageSize;
    if (pageContentLength >= pageSize) {
      return true;
    }
    const total =
      result?.totalCount ??
      result?.total ??
      result?.totalElements ??
      result?.totalRecords;
    if (typeof total === "number" && total > 0) {
      const loadedSoFar = (page - 1) * pageSize + pageContentLength;
      return loadedSoFar < total;
    }
    return false;
  }

  /**
   * Fetches one page from `ticket/v1/ticket/cx/list`.
   * @param append When false (page 1), replaces sidebar tickets in the ORM; when true, merges.
   */
  static async fetchTicketListPage(
    page: number,
    options: { append?: boolean; query?: string } = {},
  ): Promise<{ hasMore: boolean; itemCount: number; succeeded: boolean }> {
    const append = options.append === true;
    const query = options.query ?? "";
    const ticketPaginationObject = EntitySyncManager.buildTicketListPagination(
      page,
      query,
    );

    try {
      const [result]: [any, entityProcessConfig | null] =
        await EntityManager.getOnlyAPCallForEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.FillTickets],
          ticketPaginationObject,
        );

      const content: any[] =
        result != null && Array.isArray(result.content) ? result.content : [];

      if (page === 1) {
        (window as any).tickets = content;
      }

      if (!append) {
        const newIds = new Set(content.map((t) => String(t.ticketId ?? "")));
        await ticketEntity.delete((ticket: any) => {
          const id = String(ticket.ticketId);
          if (id === "0") return false;
          return !newIds.has(id);
        });
      }

      if (content.length > 0) {
        const selectedTicketId = store.getters[
          "ticketDetailModule/getTicketId"
        ] as string;
        const viewerContactId = store.getters[
          "ContactModule/getContactId"
        ] as number;
        const rows = content.map((ticket: any) => {
          const row = { ...ticket };
          const existing = ticketEntity.find(String(row.ticketId ?? ""));
          applyClientPortalUnreadStateOnTicketSync(
            row,
            selectedTicketId,
            existing?.isUnread,
            viewerContactId,
          );
          return row;
        });
        await ticketEntity.insertOrUpdate({ data: rows });
        bumpTicketSidebarListRevision();
      }

      return {
        hasMore: EntitySyncManager.resolveTicketListHasMore(
          result,
          page,
          content.length,
        ),
        itemCount: content.length,
        succeeded: true,
      };
    } catch (e) {
      await ticketEntity.update({
        where: (ticket: any) => ticket.ticketId == "0",
        data: { isFilling: false },
      });
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.fetchTicketListPage",
          e,
        ),
      );
      return { hasMore: false, itemCount: 0, succeeded: false };
    }
  }

  static async fillTickets(query = "") {
    store.commit("ticketDetailModule/SET_TicketFillingState", false);
    await EntitySyncManager.fetchTicketListPage(1, { append: false, query });
    store.commit("ticketDetailModule/SET_TicketFillingState", true);
  }

  static async fillProjects() {
    try {
      const result = await projectService.GetProjectList(
        new ProjectPaginationValues(1, 50),
      );
      const content = Array.isArray(result) ? result : [];
      await syncProjectListToStore(content, true);
      updateProjectListSessionCache(1, content.length >= 50);
      bumpProjectSidebarListRevision();
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText("EntitySyncManager.fillProjects", e),
      );
    }
  }

  static async fillClients() {
    try {
      const []: [any, entityProcessConfig | null] =
        await EntityManager.updateEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.FillClients],
          "",
          null,
        );
    } catch (e) {
      await clientEntity.update({
        where(client: any) {
          return client.clientId == 0;
        },
        data: { isFilling: false },
      });
      throw new Error(
        ErrorTemplateBuilder.getCatchText("EntitySyncManager.fillClients", e),
      );
    }
  }

  static async fillClientLocations() {
    try {
      const []: [any, entityProcessConfig | null] =
        await EntityManager.updateEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.FillClientlocations],
          "",
          null,
        );
    } catch (e) {
      await clientLocationEntity.update({
        where(clientLocation: any) {
          return clientLocation.clientId == 0;
        },
        data: { isFilling: false },
      });
      console.error(
        ErrorTemplateBuilder.getCatchText("EntitySyncManager.fillClients", e),
      );
    }
  }

  static async fillContacts() {
    try {
      const []: [any, entityProcessConfig | null] =
        await EntityManager.updateEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.FillContacts],
          "",
          null,
        );

      console.log("FILLED CONTACT ENTITY");
    } catch (error) {
      await contactEntity.update({
        where: (contact: any) => contact.contactId == 0,
        data: { isFilling: false },
      });
      console.error("Error Occured in filling Contacts: " + error);
    }
  }

  static async fillTechnicians() {
    try {
      const []: [any, entityProcessConfig | null] =
        await EntityManager.updateEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.FillTechnicians],
          "",
          null,
        );
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.fillTechnicians",
          e,
        ),
      );
    }
    await technicianEntity.update({
      where: (technician: any) => technician.technicianId == 0,
      data: { isFilling: false },
    });
  }

  static async fillLoggedInClient() {
    try {
      let clientId: number | null = store.getters["ContactModule/getClientId"];
      if (clientId !== null) {
        let client = await clientApiService.getClientDetailById(clientId);
        console.log("CLIENT FROM API");
        console.log(client);
        client["loggedIn"] = true;
        await clientEntity.insertOrUpdate({ data: client });
        console.log("fillLoggedinclient");
        console.log(clientEntity.all());
      }
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.fillLoggedInClient",
          e,
        ),
      );
    }
  }

  static async fillEmailConfigurationInfo() {
    try {
      let clientId: number = store.getters["ContactModule/getClientId"] ?? "";
      let clientGroupsAndEmailConfiguration =
        await TechnicianServices.getClientGroupsAndEmailConfiguration(clientId);
      if (
        clientGroupsAndEmailConfiguration != null &&
        clientGroupsAndEmailConfiguration.length !== 0
      ) {
        let clientGroupIds: number[] = clientGroupsAndEmailConfiguration.map(
          (group: any) => group.technicianGroupId,
        );
        store.commit("ContactModule/SET_ClientGroupIds", clientGroupIds);
        store.commit(
          "ContactModule/SET_EmailConfigurationId",
          clientGroupsAndEmailConfiguration[0].EmailConfigurationId,
        );
      }
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.fillEmailConfigurationInfo",
          e,
        ),
      );
    }
  }

  static async fillFormList() {
    try {
      store.commit("MasterModule/SET_FORM_ENTITY_LOADED", false);

      let contactId: number = store.getters["ContactModule/getContactId"];
      let formDrafts = await formApiService.getFormDraftList(contactId);
      let formList = await formApiService.getFormList();

      // Allow In Portal, client and contact-tag rules in one place (shared with the Messenger).
      const contact = store.getters["ContactModule/getContact"];
      const viewer = {
        clientId: store.getters["ContactModule/getClientId"] as number | null,
        tagIds: contact?.tagIds as number[] | undefined,
      };
      const filteredFormList = formList.filter((form: any) =>
        isFormVisibleToContact(form, viewer),
      );

      if (formDrafts !== undefined) {
        formDrafts.forEach((item: any) => {
          item.state = "Draft";
          filteredFormList.push(item);
        });
      }

      await formEntity.insertOrUpdate({ data: filteredFormList });
      store.commit("MasterModule/SET_FORM_ENTITY_LOADED", true);
    } catch (e) {
      console.error("Error in fetching forms list");
      console.error(e);
    }
  }

  static async fillTechnicianGroup() {
    try {
      const []: [any, entityProcessConfig | null] =
        await EntityManager.updateEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.FillTechnicianGroup],
          "",
          null,
        );
    } catch (e) {
      console.log(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.fillTechnicianGroup",
          e,
        ),
      );
    }
    await technicianGroupEntity.update({
      where: (technicianGroup: any) => technicianGroup.technicianGroupId == 0,
      data: { isFilling: false },
    });
  }

  static async fillProjectType() {
    try {
      const []: [any, entityProcessConfig | null] =
        await EntityManager.updateEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.FillProjectTypes],
          "",
          null,
        );
    } catch (e) {
      console.log(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.fillProjectType",
          e,
        ),
      );
    }
  }

  static async getEmailConfigurations() {
    try {
      await EntityManager.updateEntity(
        { eventId: Enums.StateEntitySync.InsertOrUpdate },
        Enums.EntitySyncManager[Enums.EntitySyncManager.GetEmailConfigurations],
        "",
        null,
      );
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.getEmailConfigurations",
          e,
        ),
      );
    }
  }

  static async fillMSPTicketSettings() {
    try {
      await EntityManager.updateEntity(
        { eventId: Enums.StateEntitySync.InsertOrUpdate },
        Enums.EntitySyncManager[Enums.EntitySyncManager.FillMSPTicketSettings],
        "",
        "",
      );
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.FillMSPTicketSettings",
          e,
        ),
      );
    }
  }

  static AddPropertiesToTickets(tickets: any) {
    if (tickets) {
      for (let i = 0; i < tickets.length; i++) {
        tickets[i].jsCreatedOn = DateTime.fromISO(tickets[i].createdOn, {
          zone: "utc",
        }).toJSDate();
        tickets[i].jsUpdatedOn = DateTime.fromISO(tickets[i].updatedOn, {
          zone: "utc",
        }).toJSDate();
        tickets[i].statusSortOrder = CommonHelper.getTicketStatusSortOrder(
          tickets[i].ticketStatusId,
        );
      }
    }
    return tickets;
  }

  static async fillContactTags() {
    try {
      await EntityManager.updateEntity(
        { eventId: Enums.StateEntitySync.InsertOrUpdate },
        Enums.EntitySyncManager[Enums.EntitySyncManager.FillContactTags],
        "",
        null,
      );
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.fillContactTags",
          e,
        ),
      );
    }
  }

  static async fillCustomStatus() {
    try {
      const []: [any, entityProcessConfig | null] =
        await EntityManager.updateEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.FillCustomStatus],
          "",
          null,
        );
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText("EntitySyncManager.fillClients", e),
      );
    }
  }

  static async fillTicketTypes() {
    try {
      const result = await ticketApiService.getTicketTypeList();
      if (result) {
        await ticketTypeEntity.insertOrUpdate({ data: result });
      }
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.fillTicketTypes",
          e,
        ),
      );
    }
  }

  static async fillTags() {
    try {
      const result = await ticketApiService.getTicketTagsList();
      if (result) {
        await tagEntity.insertOrUpdate({ data: result });
      }
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText("EntitySyncManager.fillTags", e),
      );
    }
  }

  static async fetchTicketTypeSettings() {
    try {
      const result = await ServiceProviderServices.getTicketTypeSettings();
      if (result) {
        await ticketTypeSettingEntity.insertOrUpdate({ data: result });
        if (result.isEnabled) {
          await EntitySyncManager.fillTicketTypes();
        }
      }
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntitySyncManager.fetchTicketTypeSettings",
          e,
        ),
      );
    }
  }
}
